import React, {useState} from 'react';
import {Dialog, DialogBody, DialogHeader, Button, Text, Icon, Spin} from '@gravity-ui/uikit';
import {ArrowDownToLine, MagnifierPlus, MagnifierMinus, Xmark} from '@gravity-ui/icons';
import {Viewer, Worker} from '@react-pdf-viewer/core';
import {zoomPlugin} from '@react-pdf-viewer/zoom';

import '@react-pdf-viewer/core/lib/styles/index.css';

import styles from './DocumentViewer.module.scss';

interface DocumentViewerProps {
  open: boolean;
  onClose: () => void;
  documentUrl: string;
  documentTitle: string;
  fileName?: string;
}

export const DocumentViewer = ({open, onClose, documentUrl, documentTitle, fileName}: DocumentViewerProps) => {
  const [error, setError] = useState<string | null>(null);
  const zoomPluginInstance = zoomPlugin();
  const {ZoomIn, ZoomOut} = zoomPluginInstance;

  React.useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open, documentUrl]);

  const handleDocumentLoad = () => {
    setError(null);
  };

  const handleDocumentError = () => {
    setError('Ошибка при загрузке документа');
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName || documentTitle;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderDocumentContent = () => {
    const fileExtension = documentUrl.split('.').pop()?.toLowerCase();

    if (fileExtension === 'pdf') {
      return (
        <div className={styles.pdfContainer}>
          <div className={styles.pdfToolbar}>
            <ZoomIn>
              {(props) => (
                <Button view="normal" size="l" onClick={props.onClick}>
                  <Icon data={MagnifierPlus} size={16} className={styles.zoomIcon} />
                </Button>
              )}
            </ZoomIn>
            <ZoomOut>
              {(props) => (
                <Button view="normal" size="l" onClick={props.onClick}>
                  <Icon data={MagnifierMinus} size={16} className={styles.zoomIcon} />
                </Button>
              )}
            </ZoomOut>
          </div>

          <Worker workerUrl="/pdf.worker.min.js">
            <Viewer
              fileUrl={documentUrl}
              renderLoader={() => <Spin size="xl" />}
              defaultScale={1}
              plugins={[zoomPluginInstance]}
              onDocumentLoad={() => {
                handleDocumentLoad();
              }}
              renderError={() => (
                <div className={styles.errorContainer}>
                  <Text variant="header-2" color="danger">
                    Ошибка загрузки PDF
                  </Text>
                  <Text variant="body-1">Не удалось загрузить PDF документ</Text>
                  <Button size="l" view="action" onClick={handleDownload}>
                    <Icon data={ArrowDownToLine} size={16} />
                    Скачать файл
                  </Button>
                </div>
              )}
            />
          </Worker>
        </div>
      );
    }

    if (['jpg', 'jpeg', 'png'].includes(fileExtension || '')) {
      return (
        <div className={styles.imageContainer}>
          <img
            src={documentUrl}
            alt={documentTitle}
            className={styles.documentImage}
            onLoad={handleDocumentLoad}
            onError={handleDocumentError}
          />
        </div>
      );
    }

    // Для остальных файлов показываем предложение скачать
    return (
      <div className={styles.downloadContainer}>
        <Text variant="header-2">Предварительный просмотр недоступен</Text>
        <Text variant="body-1" className={styles.downloadDescription}>
          Данный тип файла не поддерживает предварительный просмотр. Вы можете скачать файл для просмотра.
        </Text>
        <Button size="l" view="action" onClick={handleDownload}>
          <Icon data={ArrowDownToLine} size={16} />
          Скачать файл
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} className={styles.dialog} size="l" hasCloseButton={false}>
      <DialogHeader
        caption={documentTitle}
        className={styles.header}
        insertAfter={
          <div className={styles.headerActions}>
            <Button size="l" view="flat" onClick={handleDownload}>
              <Icon data={ArrowDownToLine} size={16} className={styles.downloadIcon} />
            </Button>
            <Button size="l" view="flat" onClick={onClose}>
              <Icon data={Xmark} size={16} className={styles.downloadIcon} />
            </Button>
          </div>
        }
      />

      <DialogBody className={styles.body}>
        {error && (
          <div className={styles.errorContainer}>
            <Text variant="header-2" color="danger">
              Ошибка загрузки
            </Text>
            <Text variant="body-1">{error}</Text>
            <Button size="l" view="action" onClick={handleDownload}>
              <Icon data={ArrowDownToLine} size={16} />
            </Button>
          </div>
        )}

        {!error && renderDocumentContent()}
      </DialogBody>
    </Dialog>
  );
};
